import { useRef, useState } from 'react';
import type { RefObject } from 'react';
import { AddOfficersTab } from './tabs/AddOfficersTab';
import { MaxCounterDurationField } from '@/components/MaxCounterDurationField';
import { HandoffOverlapToggle } from '@/components/HandoffOverlapToggle';
import type { OfficerMetadata, OperationMessage, OperationResult, RosterState } from '@/types/api';
import { UI_SCALE } from '@/utils/uiScale';

interface AddOfficersDrawerProps {
  open: boolean;
  onClose: () => void;
  subTab: 'main' | 'sos' | 'ot';
  onSubTabChange: (sub: 'main' | 'sos' | 'ot') => void;
  generated: boolean;
  /** The mutation's own snapshot is forwarded so the caller can patch the grid instantly. */
  onAdded: (result?: Pick<OperationResult, 'snapshot'>) => void;
  /** Full state returned by an undo (cancelling an add) — applied as-is. */
  onStateRestored?: (state: RosterState) => void;
  mode?: 'arrival' | 'departure';
  shift?: 'morning' | 'night';
  onOperationMessage?: (msg: OperationMessage) => void;
  /** Vertical split mode: slide up from bottom instead of from the right */
  fromBottom?: boolean;
  /** True only for touch tablets (iPad) — they can choose bottom sheet vs right panel;
   * desktop is always right, phone is always bottom, and neither shows the toggle. */
  canChoosePlacement?: boolean;
  placement?: 'bottom' | 'right';
  onChoosePlacement?: (placement: 'bottom' | 'right') => void;
  /** Existing roster officers — used for paste duplicate-detection in the SOS tab. */
  officerMetadata?: Record<string, OfficerMetadata>;
  /** Lets the caller measure the rendered drawer panel (e.g. to pin a docked bar above it). */
  panelRef?: RefObject<HTMLDivElement>;
}

// Underline style, like the main Counter/Officers/Settings tab strip — a flat
// label with its own colored segment of the shared baseline directly beneath
// it when active, not a filled pill. A filled pill reads as a pressed button;
// these three are a tab switcher, not three separate actions.
const TAB_STYLES = {
  main: { color: '#0369a1' },
  sos:  { color: '#9d174d' },
  ot:   { color: '#a16207' },
} as const;

function SubTabButtons({ subTab, onSubTabChange }: { subTab: 'main' | 'sos' | 'ot'; onSubTabChange: (s: 'main' | 'sos' | 'ot') => void }) {
  return (
    <>
      {(['main', 'sos', 'ot'] as const).map(tab => {
        const active = subTab === tab;
        return (
          <button
            key={tab}
            onClick={() => onSubTabChange(tab)}
            style={{
              flex: 1,
              // minWidth: 0 lets a flex item shrink below its text's natural width —
              // without it, at a narrow drawer width (a right-panel drawer on an
              // iPad, say) these buttons refuse to shrink and get hard-clipped by
              // the drawer's own overflow instead of shrinking their label.
              minWidth: 0,
              padding: '6px 8px',
              marginBottom: '-2px',
              background: 'transparent',
              border: 'none',
              borderBottom: active ? `3px solid ${TAB_STYLES[tab].color}` : '3px solid transparent',
              fontWeight: active ? 700 : 500,
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: active ? TAB_STYLES[tab].color : '#64748b',
            }}
          >
            {tab === 'main' ? 'Main' : tab === 'sos' ? 'SOS Officers' : 'OT Officers'}
          </button>
        );
      })}
    </>
  );
}

export function AddOfficersDrawer({
  open,
  onClose,
  subTab,
  onSubTabChange,
  generated,
  onAdded,
  onStateRestored,
  mode,
  shift,
  onOperationMessage,
  fromBottom = false,
  canChoosePlacement = false,
  placement = 'bottom',
  onChoosePlacement,
  officerMetadata,
  panelRef,
}: AddOfficersDrawerProps) {
  // Opens at 2/3 (the maximised size); the toggle shrinks it back to 1/3.
  const [maximized, setMaximized] = useState(true);
  // No external ref passed in some call sites — a local fallback keeps this usable
  // either way without needing to merge two refs onto one element.
  const fallbackPanelRef = useRef<HTMLDivElement>(null);
  const effectivePanelRef = panelRef ?? fallbackPanelRef;

  // Deliberately no outside-click-to-close: the drawer sits beside (or below) the
  // Counter/Officer panes like a split-screen pane of its own, and the two need to
  // work independently. A prior version closed on any pointerdown outside the panel,
  // which meant starting a scroll or drag on the grid — a touch-drag, a scrollbar
  // drag, even a plain click to pan — began with a pointerdown there and slammed the
  // drawer shut, silently discarding whatever was pasted/extracted. Closing is now
  // only ever explicit: the ✕ button, or a successful add (onSwitchTab/onClose below).

  if (!open) return null;

  const drawerClass = [
    'add-officers-drawer',
    fromBottom ? 'add-officers-drawer--bottom' : '',
    maximized ? 'add-officers-drawer--maximized' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Drawer panel — right-side normally; bottom sheet on mobile or in vertical split */}
      <div ref={effectivePanelRef} className={drawerClass} role="dialog" aria-modal="true">

        {/* Sticky header — the drawer's only title: name, cap and close on one
            line, with the sub-tabs on a second line (desktop) or in the footer
            (mobile). The tab body carries no heading of its own. */}
        <div style={{
          display: 'flex', flexDirection: 'column', padding: '8px 14px 0',
          gap: '4px', flexShrink: 0, zoom: UI_SCALE,
          background: 'white', position: 'sticky', top: 0, zIndex: 10,
        }}>
          {/* Left container (title + Max ctr) wraps its own content onto a second
              line when the drawer is narrow (a right-panel drawer on an iPad, say);
              the right container (placement/maximize/close) never wraps and stays
              pinned top-right, always reachable regardless of width. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', flex: '1 1 auto', minWidth: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', whiteSpace: 'nowrap', flexShrink: 0 }}>
                Add Officers
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              {/* Touch-tablet only: bottom sheet is the default everywhere else on
                  tablet/phone, but a tablet has the width to make a right panel
                  worthwhile too — picking it also stacks Counter/Officer to leave
                  each pane readable next to the narrower drawer. */}
              {canChoosePlacement && (
                <button
                  onClick={() => onChoosePlacement?.(placement === 'bottom' ? 'right' : 'bottom')}
                  title={placement === 'bottom' ? 'Switch to right-side panel (stacks Counter & Officer)' : 'Switch to bottom sheet'}
                  style={{
                    background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '6px',
                    cursor: 'pointer', color: '#444',
                    padding: '5px 10px', lineHeight: 1, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {/* "Dock panel" icon — the filled strip shows where the drawer will
                      move TO, so it reads as a layout toggle rather than a download
                      (a plain down-arrow) or a refresh (circular-arrow) glyph. */}
                  {placement === 'bottom' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <rect x="15" y="4" width="6" height="16" rx="1" fill="currentColor" stroke="none" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <rect x="3" y="14" width="18" height="6" rx="1" fill="currentColor" stroke="none" />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={() => setMaximized(m => !m)}
                title={maximized ? 'Restore drawer size' : 'Maximize drawer — more room to scroll pasted/extracted text'}
                style={{
                  background: maximized ? '#e0f2fe' : 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', color: maximized ? '#0369a1' : '#444',
                  padding: '5px 10px', lineHeight: 1, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {maximized ? (
                  // Minimize: two arrows along the top-right → bottom-left diagonal, pointing inward at each other.
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 9 20 4" />
                    <polyline points="15 9 15 4" />
                    <polyline points="15 9 20 9" />
                    <polyline points="9 15 4 20" />
                    <polyline points="9 15 9 20" />
                    <polyline points="9 15 4 15" />
                  </svg>
                ) : '⛶'}
              </button>
              <button
                onClick={onClose}
                title="Close"
                style={{
                  background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '6px',
                  fontSize: '18px', cursor: 'pointer', color: '#444',
                  padding: '5px 10px', lineHeight: 1, flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          </div>
          {/* Desktop: tabs here; hidden on mobile via .drawer-header-tabs */}
          <div className="drawer-header-tabs">
            <SubTabButtons subTab={subTab} onSubTabChange={onSubTabChange} />
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {/* Shared by all three sub-tabs — every one of them can start an auto
              break assignment, and they all plan to the same cap. It scrolls with
              the body (not the sticky header): in the 1/3-height sheet the header
              plus these controls used to eat the whole drawer, leaving no room to
              scroll down to Extract / Add SOS manually. */}
          <div style={{ zoom: UI_SCALE }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', padding: '6px 14px 0' }}>
            <MaxCounterDurationField compact />
            <HandoffOverlapToggle compact />
          </div>
          <AddOfficersTab
            generated={generated}
            onAdded={onAdded}
            onStateRestored={onStateRestored}
            onSwitchTab={() => onClose()}
            subTab={subTab}
            onSubTabChange={onSubTabChange}
            mode={mode}
            shift={shift}
            onOperationMessage={onOperationMessage}
            officerMetadata={officerMetadata}
          />
          </div>
        </div>

        {/* Mobile footer — tabs here; hidden on desktop via .drawer-footer-tabs */}
        <div className="drawer-footer-tabs">
          <SubTabButtons subTab={subTab} onSubTabChange={onSubTabChange} />
        </div>
      </div>
    </>
  );
}
